'use client';

import { clearCheckoutCompleteMarker } from '@/features/checkout/utils/checkoutCompletion';
import { clientFetch } from '@/lib/api/clientFetch';
import {
  defaultLocale,
  localeCookieKey,
  resolveLocale,
} from '@/lib/i18n/config';
import { getProductCurrency } from '@/lib/products';
import {
  hasCheckoutShipping,
  hasGeolocationSelection,
  hasManualShippingAddress,
} from '@/lib/shipping';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const CART_STORAGE_KEY = 'cart';
const DEFAULT_SHIPPING = 10;
const FREE_SHIPPING_THRESHOLD = 100;

const initialCartState = {
  cartItems: [],
  shippingAddress: {},
  geolocation: {},
  paymentMethod: 'PayPal',
  uiPaymentMethod: 'PayPal',
  locale: defaultLocale,
  discount: null,
  appliedBenefits: [],
};

const CartContext = createContext(null);

function getCartLineId(item = {}) {
  const productNumber = String(item.productNumber || '').trim();

  if (typeof item.cartLineId === 'string' && item.cartLineId.trim()) {
    return item.cartLineId;
  }

  return item.isGift ? `${productNumber}::gift` : productNumber;
}

function sanitizeCartItem(item = {}, fallbackCurrency = 'USD') {
  return {
    ...item,
    cartLineId: getCartLineId(item),
    qty: Math.max(1, Number(item.qty || 1)),
    price: Number(item.price || 0),
    realPrice: Number(item.realPrice || item.price || 0),
    countInStock: Number(item.countInStock || 0),
    currency: String(item.currency || fallbackCurrency || 'USD'),
    isGift: Boolean(item.isGift),
    codEligible: Boolean(item.codEligible),
    categories: Array.isArray(item.categories) ? item.categories : [],
  };
}

function sanitizeCartItems(items = []) {
  const fallbackCurrency =
    items.find((item) => item?.currency)?.currency || 'USD';

  return (Array.isArray(items) ? items : []).map((item) =>
    sanitizeCartItem(item, fallbackCurrency),
  );
}

function stripGiftItems(items = []) {
  return sanitizeCartItems(items).filter((item) => !item.isGift);
}

function hasFreeShippingBenefit(appliedBenefits = []) {
  return Array.isArray(appliedBenefits)
    ? appliedBenefits.some((benefit) => benefit?.type === 'free_shipping')
    : false;
}

function getCheckoutLocale(currency = 'USD') {
  switch (currency) {
    case 'XOF':
      return 'fr-SN';
    case 'EUR':
      return 'fr';
    case 'USD':
    default:
      return 'en';
  }
}

function mergeGiftItems(items = [], giftItems = []) {
  const baseItems = stripGiftItems(items);
  const fallbackCurrency = baseItems[0]?.currency || 'USD';
  const nextGiftItems = (Array.isArray(giftItems) ? giftItems : []).map(
    (giftItem) =>
      sanitizeCartItem(
        {
          ...giftItem,
          cartLineId: `${giftItem.productNumber}::gift`,
          currency: giftItem.currency || fallbackCurrency,
          codEligible: false,
          isGift: true,
        },
        fallbackCurrency,
      ),
  );

  return [...baseItems, ...nextGiftItems];
}

function addDecimals(num) {
  return Number((Math.round(num * 100) / 100).toFixed(2));
}

function calculateTotals(state) {
  const cartItems = sanitizeCartItems(state.cartItems);
  const purchasableItems = cartItems.filter((item) => !item.isGift);

  if (!purchasableItems.length) {
    return {
      ...state,
      cartItems: [],
      discount: null,
      appliedBenefits: [],
      totalItems: 0,
      itemsPrice: 0,
      shippingPrice: 0,
      taxPrice: 0,
      totalPrice: 0,
      finalTotalPrice: 0,
    };
  }

  const itemsPrice = addDecimals(
    cartItems.reduce(
      (sum, item) => sum + Number(item.price || 0) * Number(item.qty || 0),
      0,
    ),
  );
  const shippingPrice = hasFreeShippingBenefit(state.appliedBenefits)
    ? 0
    : itemsPrice > FREE_SHIPPING_THRESHOLD
      ? 0
      : DEFAULT_SHIPPING;
  const totalItems = cartItems.reduce(
    (sum, item) => sum + Number(item.qty || 0),
    0,
  );
  const discountAmount = addDecimals(Number(state.discount?.amount || 0));
  const totalPrice = addDecimals(
    Math.max(itemsPrice + shippingPrice - discountAmount, 0),
  );

  return {
    ...state,
    cartItems,
    discount: state.discount
      ? {
          ...state.discount,
          amount: discountAmount,
        }
      : null,
    appliedBenefits: Array.isArray(state.appliedBenefits)
      ? state.appliedBenefits
      : [],
    totalItems,
    itemsPrice,
    shippingPrice: addDecimals(shippingPrice),
    taxPrice: 0,
    totalPrice,
    finalTotalPrice: totalPrice,
  };
}

function normalizeCartItem(product, qty = 1) {
  return {
    cartLineId: String(product.productNumber || '').trim(),
    productNumber: product.productNumber,
    name: product.name,
    coverImage: product.coverImage,
    qty,
    price: Number(product.price || 0),
    prices: product.prices || null,
    currency: getProductCurrency(product),
    countInStock: Number(product.countInStock || 0),
    codEligible: Boolean(product.codEligible),
    categories: Array.isArray(product.categories) ? product.categories : [],
    isGift: false,
  };
}

function readClientLocale() {
  if (typeof window === 'undefined') {
    return resolveLocale(defaultLocale);
  }

  const cookieLocale = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${localeCookieKey}=`))
    ?.split('=')[1];

  return resolveLocale(
    window.localStorage.getItem(localeCookieKey) ||
      (cookieLocale ? decodeURIComponent(cookieLocale) : '') ||
      window.navigator.language ||
      document.documentElement.lang ||
      defaultLocale,
  );
}

function readStoredCart() {
  if (typeof window === 'undefined') {
    return calculateTotals(initialCartState);
  }

  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    const currentLocale = readClientLocale();

    if (!raw) {
      return calculateTotals({ ...initialCartState, locale: currentLocale });
    }

    const parsed = JSON.parse(raw);

    if (parsed?.locale && resolveLocale(parsed.locale) !== currentLocale) {
      window.localStorage.removeItem(CART_STORAGE_KEY);
      return calculateTotals({ ...initialCartState, locale: currentLocale });
    }

    return calculateTotals({
      ...initialCartState,
      ...parsed,
      locale: currentLocale,
    });
  } catch {
    return calculateTotals(initialCartState);
  }
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => calculateTotals(initialCartState));
  const [hasHydrated, setHasHydrated] = useState(false);
  const couponRefreshSignature = useMemo(() => {
    if (!hasHydrated) {
      return '';
    }

    const code = String(cart.discount?.code || '')
      .trim()
      .toUpperCase();
    const couponItems = stripGiftItems(cart.cartItems);

    if (!code || !couponItems.length) {
      return '';
    }

    const currency = couponItems[0]?.currency || 'USD';
    const itemsSignature = couponItems
      .map((item) => `${item.productNumber}:${Number(item.qty || 0)}`)
      .sort()
      .join('|');

    return `${code}::${currency}::${itemsSignature}`;
  }, [hasHydrated, cart.cartItems, cart.discount?.code]);

  useEffect(() => {
    setCart(readStoredCart());
    setHasHydrated(true);
  }, []);

  useEffect(() => {
    if (!couponRefreshSignature) {
      return;
    }

    const couponItems = stripGiftItems(cart.cartItems).map((item) => ({
      productNumber: item.productNumber,
      qty: item.qty,
    }));
    const currency = stripGiftItems(cart.cartItems)[0]?.currency || 'USD';
    const code = String(cart.discount?.code || '')
      .trim()
      .toUpperCase();
    const locale = getCheckoutLocale(currency);
    let isCancelled = false;

    async function refreshCoupon() {
      try {
        const response = await clientFetch('/api/coupons/apply', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'x-lang': locale,
            'accept-language': locale,
          },
          body: JSON.stringify({
            code,
            items: couponItems,
          }),
        });

        if (isCancelled) {
          return;
        }

        setCart((current) =>
          calculateTotals({
            ...current,
            discount: {
              ...(current.discount || {}),
              code: response.code || code,
              amount: Number(response.amount || 0),
              description:
                response.description || current.discount?.description || '',
              label: response.label || current.discount?.label || '',
            },
            appliedBenefits: Array.isArray(response.appliedBenefits)
              ? response.appliedBenefits
              : Array.isArray(response.benefits)
                ? response.benefits
                : [],
            cartItems: mergeGiftItems(current.cartItems, response.giftItems),
          }),
        );
      } catch (error) {
        if (isCancelled) {
          return;
        }

        if (error?.status === 400 || error?.status === 404) {
          setCart((current) =>
            calculateTotals({
              ...current,
              cartItems: stripGiftItems(current.cartItems),
              discount: null,
              appliedBenefits: [],
            }),
          );
        }
      }
    }

    refreshCoupon();

    return () => {
      isCancelled = true;
    };
  }, [couponRefreshSignature]);

  useEffect(() => {
    if (!hasHydrated || typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(
      CART_STORAGE_KEY,
      JSON.stringify({
        ...cart,
        locale: readClientLocale(),
      }),
    );
  }, [cart, hasHydrated]);

  const value = useMemo(() => {
    const setNext = (updater) => {
      setCart((current) => calculateTotals(updater(current)));
    };

    return {
      ...cart,
      hasHydrated,
      hasItems: cart.cartItems.length > 0,
      hasAppliedCoupon: Boolean(cart.discount?.code),
      hasFreeShippingBenefit: hasFreeShippingBenefit(cart.appliedBenefits),
      hasShippingAddress: hasManualShippingAddress(cart.shippingAddress),
      hasGeolocation: hasGeolocationSelection(cart.geolocation),
      hasCheckoutShipping: hasCheckoutShipping(
        cart.shippingAddress,
        cart.geolocation,
      ),
      addItem(product, qty = 1) {
        clearCheckoutCompleteMarker();
        setNext((current) => {
          const normalized = normalizeCartItem(product, qty);
          const existing = current.cartItems.find(
            (item) =>
              item.productNumber === normalized.productNumber && !item.isGift,
          );

          return {
            ...current,
            cartItems: existing
              ? current.cartItems.map((item) =>
                  item.productNumber === normalized.productNumber &&
                  !item.isGift
                    ? {
                        ...item,
                        ...normalized,
                        qty: Math.min(
                          Number(
                            item.countInStock || normalized.countInStock || 99,
                          ),
                          Number(item.qty || 0) + Number(qty || 1),
                        ),
                      }
                    : item,
                )
              : [...current.cartItems, normalized],
          };
        });
      },
      updateQty(cartLineId, qty) {
        const nextQty = Math.max(1, Number(qty || 1));
        setNext((current) => ({
          ...current,
          cartItems: current.cartItems.map((item) =>
            item.cartLineId === cartLineId
              ? {
                  ...item,
                  qty: Math.min(item.countInStock || nextQty, nextQty),
                }
              : item,
          ),
        }));
      },
      removeItem(cartLineId) {
        setNext((current) => ({
          ...current,
          cartItems: current.cartItems.filter(
            (item) => item.cartLineId !== cartLineId,
          ),
        }));
      },
      applyDiscount(discountPayload = {}) {
        setNext((current) => ({
          ...current,
          discount: discountPayload.code
            ? {
                code: discountPayload.code,
                amount: Number(discountPayload.amount || 0),
                description: discountPayload.description || '',
                label: discountPayload.label || '',
              }
            : null,
          appliedBenefits: Array.isArray(discountPayload.appliedBenefits)
            ? discountPayload.appliedBenefits
            : [],
          cartItems: mergeGiftItems(
            current.cartItems,
            discountPayload.giftItems,
          ),
        }));
      },
      clearDiscount() {
        setNext((current) => ({
          ...current,
          cartItems: stripGiftItems(current.cartItems),
          discount: null,
          appliedBenefits: [],
        }));
      },
      saveShippingAddress(shippingAddress) {
        setNext((current) => ({
          ...current,
          shippingAddress,
          geolocation: { ...current.geolocation, useGeolocation: false },
        }));
      },
      saveGeolocation(geolocation) {
        const { phone, ...nextGeolocation } = geolocation || {};

        setNext((current) => ({
          ...current,
          shippingAddress: {
            ...current.shippingAddress,
            ...(typeof phone === 'string' && phone.trim()
              ? { phone: phone.trim() }
              : {}),
          },
          geolocation: {
            ...current.geolocation,
            ...nextGeolocation,
            useGeolocation: true,
          },
        }));
      },
      savePaymentMethod(paymentMethod, nextUiPaymentMethod) {
        setNext((current) => ({
          ...current,
          paymentMethod,
          uiPaymentMethod:
            typeof nextUiPaymentMethod === 'string'
              ? nextUiPaymentMethod
              : paymentMethod,
        }));
      },
      clearCart() {
        setNext((current) => ({
          ...current,
          cartItems: [],
          discount: null,
          appliedBenefits: [],
        }));
      },
      resetCart(nextLocale = readClientLocale()) {
        clearCheckoutCompleteMarker();

        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(CART_STORAGE_KEY);
        }

        setCart(
          calculateTotals({
            ...initialCartState,
            locale: resolveLocale(nextLocale),
          }),
        );
      },
    };
  }, [cart, hasHydrated]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const value = useContext(CartContext);

  if (!value) {
    throw new Error('useCart must be used within CartProvider');
  }

  return value;
}
